import { Component, OnInit, ViewChild, inject, signal } from '@angular/core'
import { EnqueteService } from '../../services/enquete.service'
import { EnqueteDto } from '../../models/enquete-dto'
import { ToastrService } from 'ngx-toastr'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { getTypeUseCaseName, MailDto, typeUseCases } from '../../models/mail-dto'
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { compareNullableNumbers, compareRawDates, deepCopy, formatDateForBack, formatDateFromBack, lancementIsValid, scrollToTop, shouldDisplayInvalid } from '../../utils/utils'
import { typeStatus } from '../../models/sat-dto'
import { environment } from '../../../environments/environment'
import { NgClass } from '@angular/common'
import { FormatDateFromBack } from '../../pipes/format-date-from-back.pipe'
import { AgGridAngular } from 'ag-grid-angular'
import { ColDef, RowClickedEvent, SortDirection } from 'ag-grid-community'
import { localeFr } from '../../../assets/localization/ag-grid/ag-grid-locale-fr'

@Component({
    selector: 'app-admin',
    templateUrl: './admin.component.html',
    styleUrls: ['./admin.component.scss'],
    imports: [FormsModule, ReactiveFormsModule, NgClass, FormatDateFromBack, AgGridAngular],
    standalone: true,
})
export class AdminComponent implements OnInit {
    enqueteService = inject(EnqueteService)
    toastr = inject(ToastrService)
    modalService = inject(NgbModal)
    private readonly formBuilder = inject(FormBuilder)

    enquetes = signal<EnqueteDto[]>([])
    selectedEnquete = signal<EnqueteDto | null>(null)
    working = signal(false)
    @ViewChild('viewEnqueteModal') viewEnqueteModal: any
    @ViewChild('createEmailModal') createEmailModal: any
    @ViewChild('updateStatusModal') updateStatusModal: any
    createEmailForm: FormGroup
    updateStatusForm: FormGroup
    buildMailLinkForm: FormGroup
    readonly localeFr = localeFr
    public colDefs: ColDef[] = []
    defaultColDef: ColDef = {
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        filter: 'agTextColumnFilter',
        filterParams: { maxNumConditions: 1, filterOptions: ['contains', 'equals', 'notContains', 'notEqual'] },
    }
    protected readonly getTypeUseCaseName = getTypeUseCaseName
    protected readonly shouldDisplayInvalid = shouldDisplayInvalid
    protected readonly typeStatus = typeStatus

    ngOnInit(): void {
        this.working.set(true)

        this.buildColumnDefs()

        this.enqueteService.getEnquetes().subscribe((value) => {
            value.sort((a, b) => +b.updateDate - +a.updateDate)
            this.enquetes.set(value)
            this.working.set(false)
        })

        this.createEmailForm = this.formBuilder.group({
            emailType: [null, [Validators.required]],
            sentCount: [null, [Validators.required, Validators.min(0)]],
            lastSentDate: [null, [Validators.required]],
        })

        this.updateStatusForm = this.formBuilder.group({
            status: [null, [Validators.required]],
        })

        this.buildMailLinkForm = this.formBuilder.group({
            statutLancement: ['VALIDE', [Validators.required]],
            gestionSav: [null],
            delayedLaunch: [false],
        })
    }

    buildColumnDefs(): void {
        this.colDefs = [
            {
                name: 'Id Enquête',
                getter: (params: any) => (params.data.id ? params.data.id : '-'),
            },
            {
                name: 'Type Enquête',
                getter: (params: any) => (params.data.typeEnquete ? params.data.typeEnquete : '-'),
            },
            {
                name: 'Statut',
                getter: (params: any) => (params.data.sat?.lancement ? params.data.sat?.lancement : '-'),
            },
            {
                name: 'Date Lancement',
                getter: (params: any) => (params.data.sat?.launchDate ? formatDateFromBack(params.data.sat?.launchDate) : '-'),
                comparator: (_valueA: any, _valueB: any, nodeA: any, nodeB: any) => compareRawDates(nodeA.data?.sat?.launchDate, nodeB.data?.sat?.launchDate),
            },
            {
                name: 'Code Affaire',
                getter: (params: any) => (params.data.codeAffaire ? params.data.codeAffaire : '-'),
            },
            {
                name: 'Code Entité Principal',
                getter: (params: any) => (params.data.codeEntitePrincipale ? params.data.codeEntitePrincipale : '-'),
            },
            {
                name: 'Nom Affaire',
                getter: (params: any) => (params.data.nomAffaire ? params.data.nomAffaire : '-'),
            },
            {
                name: 'Code Chantier',
                getter: (params: any) => (params.data.codeChantier ? params.data.codeChantier : '-'),
            },
            {
                name: 'Tractation TCE',
                getter: (params: any) => (params.data.montantTractationTCE ? params.data.montantTractationTCE : '-'),
                comparator: (_valueA: any, _valueB: any, nodeA: any, nodeB: any) => compareNullableNumbers(nodeA.data?.montantTractationTCE, nodeB.data?.montantTractationTCE),
            },
            {
                name: 'Cumul Origine',
                getter: (params: any) => (params.data.cumulOrig ? params.data.cumulOrig : '-'),
                comparator: (_valueA: any, _valueB: any, nodeA: any, nodeB: any) => compareNullableNumbers(nodeA.data?.cumulOrig, nodeB.data?.cumulOrig),
            },
            {
                name: 'Complétion',
                getter: (params: any) => this.buildCompletionLabel(params.data),
                comparator: (_valueA: any, _valueB: any, nodeA: any, nodeB: any) => {
                    const valA = nodeA.data?.montantTractationTCE && nodeA.data?.cumulOrig ? (nodeA.data.cumulOrig / nodeA.data.montantTractationTCE) : 0
                    const valB = nodeB.data?.montantTractationTCE && nodeB.data?.cumulOrig ? (nodeB.data.cumulOrig / nodeB.data.montantTractationTCE) : 0
                    return valA - valB
                }
            },
            {
                name: 'Date de mise à jour',
                getter: (params: any) => (params.data.updateDate ? formatDateFromBack(params.data.updateDate) : '-'),
                sort: 'desc' as SortDirection,
                comparator: (_valueA: any, _valueB: any, nodeA: any, nodeB: any) => compareRawDates(nodeA.data?.updateDate, nodeB.data?.updateDate),
            },
            {
                name: 'Date de création',
                getter: (params: any) => (params.data.insertDate ? formatDateFromBack(params.data.insertDate) : '-'),
                comparator: (_valueA: any, _valueB: any, nodeA: any, nodeB: any) => compareRawDates(nodeA.data?.insertDate, nodeB.data?.insertDate),
            },
            {
                name: 'Date de Validation',
                getter: (params: any) => (params.data.sat?.dateValidation ? formatDateFromBack(params.data.sat?.dateValidation) : '-'),
                comparator: (_valueA: any, _valueB: any, nodeA: any, nodeB: any) => compareRawDates(nodeA.data?.sat?.dateValidation, nodeB.data?.sat?.dateValidation),
            },
        ].map((def): ColDef => {
            return {
                headerName: def.name,
                headerTooltip: def.name,
                sort: def.sort,
                valueGetter: def.getter,
                tooltipValueGetter: def.getter,
                flex: 2,
                comparator: def.comparator,
            }
        })
    }

    openViewEnqueteModal(event: RowClickedEvent<any, any>) {
        const item = event.data
        item.sentEmails?.sort((a: MailDto, b: MailDto) => +b?.lastSentDate - +a?.lastSentDate)
        this.selectedEnquete.set(item)
        this.modalService.open(this.viewEnqueteModal, {
            fullscreen: true,
            modalDialogClass: 'h-auto',
            backdropClass: 'bg-white opacity-100',
        })
    }

    openCreateSentMailModal() {
        this.modalService.open(this.createEmailModal)
    }

    openUpdateStatusModal() {
        this.modalService.open(this.updateStatusModal)
    }

    dismissViewEnqueteModal() {
        this.modalService.dismissAll()
        this.selectedEnquete.set(null)
    }

    dismissCreateEmailModal(modal: any) {
        modal.dismiss()
        this.createEmailForm.reset()
    }

    dismissUpdateStatusModal(modal: any) {
        modal.dismiss()
        this.updateStatusForm.reset()
    }

    deleteSentMail(mail: MailDto) {
        this.working.set(true)
        const currentSelected = this.selectedEnquete()
        if (currentSelected) {
            const copyEnquete: EnqueteDto = deepCopy(currentSelected)
            const index = copyEnquete.sentEmails.indexOf(mail, 0)
            copyEnquete.sentEmails.splice(index, 1)
            this.updateEnquetes(copyEnquete)
        }
    }

    onCreateEmailSubmit(modal: any) {
        if (this.createEmailForm.valid) {
            this.working.set(true)
            const currentSelected = this.selectedEnquete()
            if (currentSelected) {
                if (currentSelected.sentEmails.some((value) => value.typeUseCase === this.createEmailForm.get('emailType').value)) {
                    this.toastr.error('Ce type de mail existe déjà', 'Création impossible')
                    this.working.set(false)
                    return
                }

                const copyEnquete: EnqueteDto = deepCopy(currentSelected)
                copyEnquete.sentEmails.push({
                    sentCount: this.createEmailForm.get('sentCount').value,
                    lastSentDate: formatDateForBack(new Date(this.createEmailForm.get('lastSentDate').value)),
                    typeUseCase: this.createEmailForm.get('emailType').value,
                })

                this.updateEnquetes(copyEnquete).add(() => {
                    modal?.dismiss()
                    this.createEmailForm.reset()
                })
            }
        }
    }

    onEditStatusSubmit(modal: any) {
        if (this.updateStatusForm.valid) {
            this.working.set(true)
            const currentSelected = this.selectedEnquete()
            if (currentSelected) {
                const copyEnquete: EnqueteDto = deepCopy(currentSelected)
                const lancement = this.updateStatusForm.get('status').value

                copyEnquete.sat = {
                    lancement: lancement,
                    valideur: '',
                    dateValidation: lancementIsValid(lancement) ? formatDateForBack(new Date()) : '',
                    launchDate: '',
                }

                this.updateEnquetes(copyEnquete).add(() => {
                    modal?.dismiss()
                    this.updateStatusForm.reset()
                })
            }
        }
    }

    getFilteredTypeUseCases() {
        const currentSelected = this.selectedEnquete()
        if (!currentSelected) return []
        return typeUseCases.filter((value) => !currentSelected.sentEmails.some((mail) => mail.typeUseCase === value.code))
    }

    getEnquestesCsvExport() {
        this.enqueteService.exportEnquetes().subscribe((data) => {
            const now = new Date()
            const timestamp = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}-${now.getHours().toString().padStart(2, '0')}-${now.getMinutes().toString().padStart(2, '0')}-${now.getSeconds().toString().padStart(2, '0')}`
            const filename = `Export_APISAT_BDD_${environment.name.toUpperCase()}_${timestamp}.csv`

            const blob = new Blob([data], { type: 'text/csv' })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = filename
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
        })
    }

    onTriggerEmail() {
        const currentSelected = this.selectedEnquete()
        if (currentSelected) {
            this.updateEnquetes(currentSelected, true, "Envoi d'email déclenché")
        }
    }

    buildCompletionLabel(enquete: EnqueteDto) {
        if (!enquete?.montantTractationTCE || !enquete?.cumulOrig) {
            return '-'
        }

        return ((enquete.cumulOrig / enquete.montantTractationTCE) * 100.0).toFixed(2) + '%'
    }

    onBuildMailLink() {
        if (this.buildMailLinkForm.valid) {
            const currentSelected = this.selectedEnquete()
            if (currentSelected) {
                const statutLancement: string = this.buildMailLinkForm.get('statutLancement').value
                const gestionSav: boolean = this.buildMailLinkForm.get('gestionSav').value
                const delayedLaunch: boolean = this.buildMailLinkForm.get('delayedLaunch').value
                const validate: string = btoa(`enqueteId=${currentSelected.id}&statutLancement=${statutLancement}&gestionSav=${gestionSav}&delayedLaunch=${delayedLaunch}`)

                const baseurl = environment.name === 'local' ? 'http://localhost:4200' : environment.baseUrl
                const url = `${baseurl.replace('/apisat-api-rest', '')}?validate=${validate}`
                window.open(url)
            }
        }
    }

    private updateEnquetes(newEnquete: EnqueteDto, sendMail: boolean = false, toastrMessage?: string) {
        this.working.set(true)
        return this.enqueteService.updateEnquete(newEnquete, sendMail).subscribe({
            next: (response) => {
                response.sentEmails?.sort((a: MailDto, b: MailDto) => +b?.lastSentDate - +a?.lastSentDate)
                this.selectedEnquete.set(response)
                this.enquetes.update((currentEnquetes) => {
                    const filtered = currentEnquetes.filter((value) => value.id !== response.id)
                    const updated = [...filtered, response]
                    updated.sort((a, b) => +b.updateDate - +a.updateDate)
                    return updated
                })
                this.working.set(false)
                this.toastr.success(toastrMessage || 'Enquête mise à jour')
            },
            error: () => {
                scrollToTop()
                this.working.set(false)
            },
        })
    }
}
